// src/lib/managers/conversation/period.ts

/**
 * Dates reçues de l'Api-Ai (F-1.9) : seule une date ISO 8601 lisible entre dans une recherche de mails
 * ou dans l'agenda. Une date illisible (« 24/06/2026 ») ou une période qui finit avant de commencer
 * donne un message, jamais une exception ni une date devinée. Fonctions pures, appelées par le
 * ConversationManager.
 */

import { parseIsoDate } from "../../utils/date/parseIsoDate";
import { buildTextResponse } from "./textResponse";
import { ComponentData } from "./types/conversation";

// #region Messages

/** Recherche en consultation : la période illisible est ignorée, et on le dit. */
export const PERIOD_IGNORED_MESSAGE =
    "Je n'ai pas compris la période demandée : la recherche porte sur toutes les dates.";

/** Recherche avec une action : refus, car retirer la période élargirait l'action. */
export const UNREADABLE_PERIOD_FOR_ACTION_MESSAGE =
    "Je n'ai pas compris la période demandée : précisez-la pour que je puisse agir sur vos mails.";

/** Recherche dont le début est après la fin. */
export const INVERTED_PERIOD_MESSAGE =
    "La période demandée se termine avant de commencer : précisez les dates.";

/** Lecture de l'agenda ou créneaux libres sur une période illisible. */
export const UNREADABLE_AGENDA_PERIOD_MESSAGE =
    "Je n'ai pas compris la période demandée : précisez les dates.";

/** Création d'un événement dont le début ou la fin est illisible. */
export const UNREADABLE_EVENT_DATE_MESSAGE =
    "Je n'ai pas compris la date de l'événement : précisez le jour et l'heure.";

/** Réponse texte d'une date refusée : sans liste ni bouton, un tour normal qui entre dans l'historique. */
export const buildPeriodMessageResponse = (message: string): ComponentData =>
    buildTextResponse(message);

// #endregion

// #region Lecture des périodes

/** Période facultative d'une recherche de mails : bornes lues (`null` si absentes), illisible ou inversée. */
export type SearchPeriod =
    | { status: "ok"; start: Date | null; end: Date | null }
    | { status: "unreadable" }
    | { status: "inverted" };

const isAbsent = (value: unknown): boolean =>
    value === undefined || value === null || value === "";

/** Période d'une recherche de mails : chaque borne est facultative, une borne donnée doit être lisible. */
export function readSearchPeriod(
    startDate: unknown,
    endDate: unknown,
): SearchPeriod {
    const start = isAbsent(startDate) ? null : parseIsoDate(startDate);
    const end = isAbsent(endDate) ? null : parseIsoDate(endDate);

    if (
        (!isAbsent(startDate) && start === null) ||
        (!isAbsent(endDate) && end === null)
    ) {
        return { status: "unreadable" };
    }
    if (start && end && start.getTime() > end.getTime()) {
        return { status: "inverted" };
    }
    return { status: "ok", start, end };
}

/**
 * Période d'un outil d'agenda : début et fin obligatoires. `null` si l'un manque ou est illisible.
 * Sinon `start` et `end`, tels que reçus et remis dans l'ordre ; `isInverted` si la fin précédait le
 * début.
 */
export function readAgendaPeriod<T>(
    startDate: T,
    endDate: T,
): { start: T; end: T; isInverted: boolean } | null {
    const start = parseIsoDate(startDate);
    const end = parseIsoDate(endDate);
    if (!start || !end) {
        return null;
    }
    const isInverted = start.getTime() > end.getTime();
    return isInverted
        ? { start: endDate, end: startDate, isInverted }
        : { start: startDate, end: endDate, isInverted };
}

// #endregion
